import { ColumnDef } from '@tanstack/react-table';
import { t } from 'i18next';
import { Chip, Icon, Typography } from '@/components';
import { IconName } from '@/components/icons/types';
import { useCountries, useDevelopmentStatus, useSaleOptions } from '@/hooks';
import { orderTechTransactionIcons } from '@/helpers';
import { OpportunityItemList, SaleOption } from '@/features/opportunities/types';
import { Badge } from '@/components/ui/badge';
import { formatCapacity, getChipFromOpportunity } from '@/features/opportunities/helpers';
import { cn } from '@/utils/styles';
import { SortableHeader } from './sortable-capacity';

export const opportunitiesColumns = (): ColumnDef<OpportunityItemList>[] => [
  {
    accessorKey: 'name',
    header: t('Opportunity'),
    cell: ({ row }) => {
      const formattedName: string = row.getValue('name') ?? '-';
      const totalProjects: number = row.original.projects.length;
      const chip = getChipFromOpportunity(row.original);
      return (
        <div className="relative min-w-0 pt-0 leading-relaxed break-words">
          {chip && (
            <div className="absolute -top-5 left-0 z-10">
              <Chip color={chip.color}>
                <Typography as="span" size="micro" weight="semibold" className="text-left text-[8px]">
                  {chip.name}
                </Typography>
              </Chip>
            </div>
          )}
          <div className={cn(chip && 'mt-2', 'flex flex-col pt-1')}>
            <Typography as="p" size="body-2" weight="semibold" className="text-left">
              {formattedName}
            </Typography>
            {totalProjects > 1 && (
              <Typography as="span" size="body-3" weight="normal" className="text-left" color="secondary">
                {totalProjects} Projects
              </Typography>
            )}
          </div>
        </div>
      );
    },
  },
  {
    accessorKey: 'technologies',
    accessorFn: (row) => row.technologies.types,
    header: t('Technology'),
    cell: ({ row }) => {
      const technologies = row.original.technologies.types;
      return (
        <div className="flex w-100">
          {orderTechTransactionIcons(technologies || []).map((technology) => (
            <Icon
              name={technology as IconName}
              className="text-primary-700 mr-2 h-6 w-6"
              key={`${technology}-${row.id}`}
            />
          ))}
        </div>
      );
    },
    filterFn: (row, columnId, filterValue) => {
      const techsRow = row.getValue('technologies') as string[];
      return filterValue.some((value: string) => techsRow.includes(value));
    },
  },
  {
    accessorKey: 'capacity',
    accessorFn: (row) => row.technologies.capacity || row.totalMw || 0,
    header: ({ column }) => <SortableHeader column={column} />,
    cell: ({ row }) => {
      const types = row.original.technologies.types;
      const capacity = row.original.technologies.capacity;
      const totalMw = row.original.totalMw;
      const formattedCapacity = formatCapacity(types, capacity, totalMw ?? undefined);
      
      return (
        <div className="min-w-0 leading-relaxed break-words whitespace-normal">
          <Typography as="p" size="body-2" weight="normal" className="text-left">
            {formattedCapacity ?? ''}
          </Typography>
        </div>
      );
    },
    filterFn: (row, columnId, filterValue) => {
      const capacityRow = Number(row.getValue('capacity'));
      if (isNaN(capacityRow)) return false;
      return filterValue.some((range: string) => {
        if (range === '>50') {
          return capacityRow > 50;
        }
        if (range.includes('-')) {
          const [min, max] = range.split('-').map(Number);
          return capacityRow >= min && capacityRow <= max;
        }
        return false;
      });
    },
    sortingFn: (rowA, rowB, columnId) => {
      const a = Number(rowA.getValue(columnId));
      const b = Number(rowB.getValue(columnId));
      return a - b;
    },
  },
  {
    accessorKey: 'status',
    accessorFn: (row) => row.technologies?.state?.list ?? [],
    header: t('Status'),
    cell: ({ row }) => {
      const { map: mapDevelopmentStatus } = useDevelopmentStatus();
      if (row.original.technologies.types.includes('topco')) return null;
      const opportunityDevStatus = row.original.technologies.state.list.map(
        (devState: string, index: number) =>
          devState !== null && (
            <Badge key={index} variant="secondary" className="m-1 h-5 min-w-5 rounded-full bg-neutral-50 px-3">
              <Typography as="p" size="body-3" weight="normal" className="text-left">
                {mapDevelopmentStatus(devState)}
              </Typography>
            </Badge>
          )
      );
      return <div className="min-w-0 leading-relaxed break-words whitespace-normal">{opportunityDevStatus}</div>;
    },
    filterFn: (row, columnId, filterValue) => {
      const statusRow = row.getValue('status') as string[];
      return filterValue.some((value: string) => statusRow.includes(value));
    },
  },
  {
    accessorKey: 'options',
    header: t('Deal Type'),
    cell: ({ row }) => {
      const { map: mapSaleOption } = useSaleOptions();
      const formattedSaleOptions = row.original.options?.map((option: SaleOption, index: number) => (
        <Badge key={index} variant="secondary" className="bg-primary-100 m-1 h-5 min-w-5 rounded-full px-3">
          <Typography as="p" size="body-3" weight="normal" className="text-primary-800 text-left">
            {mapSaleOption(option)}
          </Typography>
        </Badge>
      ));
      return <div className="min-w-0 leading-relaxed break-words whitespace-normal">{formattedSaleOptions}</div>;
    },
    filterFn: (row, columnId, filterValue) => {
      const statusRow = row.getValue('options') as string[];
      return filterValue.some((value: string) => statusRow.includes(value));
    },
  },
  {
    accessorKey: 'country',
    accessorFn: (row) => row.location.map((location) => location.country),
    header: t('Location'),
    cell: ({ row }) => {
      const { options: countries } = useCountries();
      const transactionCountries: string[] = row.original.location.flatMap((location) =>
        Array.isArray(location.country) ? location.country : [location.country]
      );
      const countryLabels = transactionCountries
        .map((tc) => countries.find((c) => c.value === tc)?.label || tc)
        .join(', ');
      const formattedCity = row.original.location
        .map((location) => location.city.join(', '))
        .filter(Boolean)
        .join(', ');
      return (
        <>
          <div className="min-w-0">
            <Typography as="p" size="body-2" weight="normal" className="text-left">
              {countryLabels}
            </Typography>
          </div>
          {formattedCity && (
            <div className="min-w-0 leading-relaxed break-words whitespace-normal">
              <Typography as="p" size="body-3" weight="normal" className="text-left text-neutral-400">
                ({formattedCity})
              </Typography>
            </div>
          )}
        </>
      );
    },
    filterFn: (row, id, filterValue: string[]) => {
      const countryRow = row.getValue('country') as string[];
      return filterValue.some((value: string) => countryRow.includes(value));
    },
  },
  {
    accessorKey: 'typeOfRevenue',
    header: 'Type of Revenue',
    enableColumnFilter: true,
    filterFn: (row, columnId, filterValue: string[]) => {
      const revenueRow = row.getValue('typeOfRevenue') as string[];
      if (!revenueRow) return false;
      return filterValue.some((value: string) => revenueRow.includes(value));
    },
    enableHiding: true,
  },
];
